
import PropTypes from 'prop-types'
import React, { Component } from 'react'
import classnames from 'classnames'
import request from 'superagent'
import withClickOutside from 'react-click-outside'
import { InputField, Form } from '@segment/react-forms'
import { error, success } from '@segment/notify'
import { validateName, validateEmail, emailPattern, validatePassword, passwordPattern } from 'lib/validation'
import { ASSET_BASE_URL } from 'config'
import FormFooter from '../FormFooter'
import ShowHide from '../ShowHide'
import { FlexItem, FlexGrid } from '../flexgrid'
import Link from '../Link'
import Space from '../Space'
import SubmitField from '../SubmitField'
import Sheet from '../Sheet'
import Gravatar from '../Gravatar'
import WorkspaceNotificationsApp from '../../containers/WorkspaceNotificationsApp'
import Overlay from '../Overlay'
import Confirm from '../Confirm'
import Offscreen from '../Offscreen'
import Popover, {PopoverGroup, PopoverListLink} from 'ui/Popover'
import './index.css'

const analytics = {
  userDeleted () {
    window.analytics.track('User Delete Clicked')
  }
}

/**
 * Password editor thing.
 */

class PasswordEditor extends Component {
  constructor () {
    super()
    this.state = {}
  }

  render () {
    return (
      <Form onSubmit={this.onSubmit.bind(this)}>
        <InputField
          validationMessage={validatePassword}
          pattern={passwordPattern}
          onInput={this.onStorePassword.bind(this)}
          label='New Password'
          type='password'
          name='password'
          placeholder='Enter a secure password'
          autofocus
          validate
          required />
        <InputField
          label='Confirm Password'
          onInput={this.onPasswordInput.bind(this)}
          error={this.state.matchMessage}
          placeholder='Confirm new password'
          type='password'
          required />
        <Space value='30' />

        <SubmitField
          disabled={this.state.loading || this.state.matchMessage}
          loading={this.state.loading}
          label='Change password'
          showLoadingIndicator
          labelLoading='Changing...' />
      </Form>
    )
  }

  onPasswordInput (e) {
    if (this.state.password !== e.target.value) return this.setState({ matchMessage: 'Your passwords do not match' })
    this.setState({ matchMessage: '' })
  }

  onStorePassword (e) {
    this.setState({ password: e.target.value })
  }

  onSubmit (data) {
    this.setState({ loading: true })
    const { user } = this.props

    request
    .patch(`/api/users/${user.email}`)
    .send({ password: data.password })
    .set('x-csrf-token', window.segment.csrf)
    .end((err, res) => {
      this.setState({ loading: false })

      if (err || !res.ok) {
        error(err && err.message || 'We were unable to update your password.')
      } else {
        success('Your password has been changed.')
      }
    })
  }
}

PasswordEditor.propTypes = {
  user: PropTypes.object.isRequired
}

PasswordEditor.displayName = 'PasswordEditor'

/**
 * User editor thing.
 */

class UserEditor extends Component {
  constructor () {
    super()
    this.state = {}
  }

  render () {
    const { user } = this.props
    return (
      <div className='UserEditor'>
        {this.renderConfirm()}
        <Form onSubmit={this.onUpdate.bind(this)} id='edit-form'>
          <InputField
            validationMessage={validateName}
            placeholder='e.g. John Smith'
            defaultValue={user.name}
            label='Full Name'
            name='name'
            required
            autofocus
            id='name' />

          <InputField
            description='This email address will be used to send any billing notices or information.'
            placeholder='e.g. johnsmith@gmail.com'
            validationMessage={validateEmail}
            onInput={this.onEmailInput.bind(this)}
            onBlur={this.onEmailInput.bind(this)}
            pattern={emailPattern}
            error={this.state.emailError}
            defaultValue={user.email}
            label='Email'
            name='email'
            id='email'
            required />
          <Space value='30' />

          <SubmitField
            disabled={this.state.loading}
            loading={this.state.loading}
            label='Save Changes'
            showLoadingIndicator
            labelLoading='Saving...' />
        </Form>
        <Space value='20' />

        <FormFooter>
          <Link onClick={this.onConfirmRemove.bind(this)}>Delete your account</Link> and all of your source data. This is irreversible.
        </FormFooter>
      </div>
    )
  }

  onUpdate (data) {
    this.setState({ loading: true })
    const { user } = this.props
    // if the email address is unchanged, it already exists in mongo
    // and db-service will not update the `name`
    if (user.email === data.email) {
      delete data.email
    }

    request
    .patch(`/api/users/${user.email}`)
    .send(data)
    .set('x-csrf-token', window.segment.csrf)
    .end((err, res) => {
      this.setState({ loading: false })
      if (err || !res.ok) return error('There was an error updating your account.')
      success('Your account has been updated.')
      window.location.reload()
    })
  }

  onEmailInput (e) {
    const { user } = this.props
    const email = e.target.value
    // if the email address is unchanged, skip validation
    if (email === user.email) return this.setState({ emailError: null })

    request
    .head('/api/users')
    .query({ email: email })
    .end((_, res) => {
      if (res && res.status === 404) {
        this.setState({ emailError: null })
      } else {
        this.setState({ emailError: `The email ${email} already exists. Please choose another.` })
      }
    })
  }

  onConfirmRemove () {
    this.setState({ showConfirm: true })
    analytics.userDeleted()
  }

  onConfirm () {
    const { user } = this.props
    request
    .del(`/api/users/${user.email}`)
    .set('x-csrf-token', window.segment.csrf)
    .end(function (err, res) {
      if (err || !res.ok) return error('There was an error removing your account.')
      window.location.href = '/'
    })
  }

  renderConfirm () {
    const { showConfirm } = this.state
    if (!showConfirm) return

    return (
      <Overlay visible>
        <Confirm title='Remove account' confirmText='Yep, delete this account anyway!' onClose={() => this.setState({ showConfirm: false })} onConfirm={this.onConfirm.bind(this)} isDangerous>
          {this.renderWarning()}
          <p>Are you sure you want to delete your account? There’s no coming back from this!</p>
        </Confirm>
      </Overlay>
    )
  }

  renderWarning () {
    // TODO: what is `user.workspaces`?
    const { id: userId, workspaces: userWorkspaces } = this.props.user
    const workspaces = userWorkspaces
      .map(w => {
        if (w.owners.length === 1 && w.owners[0] === userId) {
          return w
        }
      })
      .filter(Boolean)

    if (!workspaces.length) {
      return
    }

    return (
      <div>
        <p>You are the sole owner of the following workspaces. By deleting your user account, you will also delete these workspaces and cancel any billing associated with them.</p><br />
        <ul>{workspaces.map((workspace, index) => <strong key={`__warning_workspace_item_${index}_${workspace.slug}`}><li>{workspace.slug}</li></strong>)}</ul><br />
      </div>
    )
  }
}

UserEditor.propTypes = {
  user: PropTypes.object.isRequired
}

UserEditor.displayName = 'UserEditor'

function _PopoverHeading (props) {
  const {title, subtitle} = props
  return (
    <div className='MainNavigation-popover-heading'>
      <h2 className='MainNavigation-popover-title'>{title}</h2>
      <p className='MainNavigation-popover-subtitle'>{subtitle}</p>
    </div>
  )
}

_PopoverHeading.displayName = '_PopoverHeading'

_PopoverHeading.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired
}

/**
 * The user thing.
 */

class _User extends Component {
  constructor () {
    super()
    this.state = {
      showUserEditor: false,
      showPasswordEditor: false
    }
  }

  render () {
    const { user } = this.props
    return (
      <div ref={ref => (this.el = ref)} className='MainNavigation-user'>
        <Popover content={this.renderDropdownMenu()}>
          <button className='MainNavigation-user-button'>
            <Offscreen>Menu</Offscreen>
            <div className='MainNavigation-user-avatar'>
              <Gravatar email={user.email} name={user.name} size={30} color='green' />
            </div>
          </button>
        </Popover>
        {this.renderPasswordEditor()}
        {this.renderUserEditor()}
      </div>
    )
  }

  renderDropdownMenu () {
    const { user, workspace, shouldShowPartnersLink, partnersLink } = this.props

    const items = [
      {
        title: user.name,
        subtitle: 'Your Account',
        items: [
          { name: 'Edit Profile', onClick: () => this.setState({ dropdownVisible: false, showUserEditor: true }) },
          { name: 'Change Password', onClick: () => this.setState({ dropdownVisible: false, showPasswordEditor: true }) },
          { name: 'Documentation', href: '/docs' },
          { name: 'Community', href: '/api/community-sso-redirect' }
        ]
      }
    ]

    if (workspace) {
      items.push({
        title: workspace.name,
        subtitle: 'Current Workspace',
        items: [
          { name: 'Settings', href: `/${workspace.slug}/settings` },
          { name: 'Team', href: `/${workspace.slug}/settings/team` }
        ]
      })
    }

    // misc
    items.push({
      items: [
        { name: 'Change Workspace', href: '/workspaces' },
        { name: 'Logout', href: '/logout' }
      ]
    })

    if (shouldShowPartnersLink) {
      // add to "misc"
      items[items.length - 1].items.unshift({
        name: 'Partner Portal',
        href: partnersLink
      })
    }

    return (
      <section>
        <PopoverGroup>
          <_PopoverHeading title={user.name} subtitle='Your Account' />
          <PopoverListLink onClick={() => this.setState({ showUserEditor: true })}>
            Edit Profile
          </PopoverListLink>
          <PopoverListLink onClick={() => this.setState({ showPasswordEditor: true })}>
            Change Password
          </PopoverListLink>
          <PopoverListLink href='/docs' rel='external'>
            Documentation
          </PopoverListLink>
          <PopoverListLink href='/api/community-sso-redirect' rel='external'>
            Community
          </PopoverListLink>
        </PopoverGroup>

        {workspace && (
          <PopoverGroup>
            <_PopoverHeading title={workspace.name} subtitle='Current Workspace' />
            <PopoverListLink href={`/${workspace.slug}/settings`} rel='external'>
              Settings
            </PopoverListLink>
            <PopoverListLink href={`/${workspace.slug}/settings/team`} rel='external'>
              Team
            </PopoverListLink>
          </PopoverGroup>
        )}

        <PopoverGroup>
          {shouldShowPartnersLink && (
            <PopoverListLink href={partnersLink} rel='external'>
              Partner Portal
            </PopoverListLink>
          )}
          <PopoverListLink href='/workspaces' rel='external'>
            Change Workspace
          </PopoverListLink>
          <PopoverListLink href='/logout' rel='external'>
            Logout
          </PopoverListLink>
        </PopoverGroup>
      </section>
    )
  }

  renderPasswordEditor () {
    if (!this.state.showPasswordEditor) return
    return (
      <Sheet onClose={() => this.setState({ showPasswordEditor: false })}>
        <PasswordEditor user={this.props.user} />
      </Sheet>
    )
  }

  renderUserEditor () {
    if (!this.state.showUserEditor) return
    return (
      <Sheet onClose={() => this.setState({ showUserEditor: false })}>
        <UserEditor user={this.props.user} />
      </Sheet>
    )
  }
}

_User.propTypes = {
  partnersLink: PropTypes.string,
  shouldShowPartnersLink: PropTypes.bool,
  user: PropTypes.object.isRequired,
  workspace: PropTypes.object
}

_User.displayName = '_User'

/**
 * Enhance the `User` component with a click-outside handler.
 */

export const User = withClickOutside(_User)

/**
 * Nav item.
 */

const MainNavigationItem = props => {
  const { href, active, disabled, children } = props
  const className = classnames({
    'MainNavigation-item': true,
    'is-disabled': disabled
  })

  if (disabled) {
    return <span className={className} data-active={active}>{children}</span>
  }

  return <a className={className} href={href} rel='external' data-is-active={active}>{children}</a>
}

MainNavigationItem.displayName = 'MainNavigationItem'

MainNavigationItem.propTypes = {
  href: PropTypes.string,
  active: PropTypes.bool,
  disabled: PropTypes.bool,
  children: PropTypes.node
}

function AddButton (props) {
  const {
    workspaceSlug,
    sourcesAvailable,
    onLinkClick,
    onButtonClick
  } = props

  const AddPopoverContent = () => (
    <div>
      <PopoverListLink
        href={sourcesAvailable ? `/${workspaceSlug}/sources/catalog` : null}
        rel='external'
        onClick={() => onLinkClick('source')}
      >
        Add Source
      </PopoverListLink>

      <PopoverListLink
        href={`/${workspaceSlug}/integrations/catalog`}
        rel='external'
        onClick={() => onLinkClick('integration')}
      >
        Add Integration
      </PopoverListLink>

      <PopoverListLink onClick={() => onLinkClick('warehouse')}>
        Add Warehouse
      </PopoverListLink>

      <PopoverListLink
        href={`/${workspaceSlug}/settings/team`}
        rel='external'
        onClick={() => onLinkClick('teammember')}
      >
        Invite Team Member
      </PopoverListLink>
    </div>
  )

  return (
    <div>
      <div className='MainNavigation-add'>
        <Popover content={<AddPopoverContent />} onOpen={onButtonClick}>
          <button className='MainNavigation-add-button' type='button'>
            <Offscreen>Add</Offscreen>
            <span className='MainNavigation-add-button-wrapper'>
              <svg width='16' height='16' viewBox='0 0 16 16' xmlns='http://www.w3.org/2000/svg'>
                <path fill='#FFF' d='M15 7H9V1c0-.6-.4-1-1-1S7 .4 7 1v6H1c-.6 0-1 .4-1 1s.4 1 1 1h6v6c0 .6.4 1 1 1s1-.4 1-1V9h6c.6 0 1-.4 1-1s-.4-1-1-1z' />
              </svg>
            </span>
          </button>
        </Popover>
      </div>
    </div>
  )
}

AddButton.displayName = 'AddButton'

AddButton.propTypes = {
  workspaceSlug: PropTypes.string.isRequired,
  sourcesAvailable: PropTypes.bool.isRequired,
  onButtonClick: PropTypes.func.isRequired,
  onLinkClick: PropTypes.func.isRequired
}

/**
 * Main navigation.
 */

class MainNavigation extends Component {
  render () {
    const {
      active,
      user,
      workspace,
      hideLeftNav,
      isLockedOut,
      sourcesAvailable,
      shouldShowSourceRestriction,
      shouldShowWarehouseRestriction,
      shouldShowWorkspaceNotifications,
      onAddButtonClick,
      onAddLinkClick,
      onRestrictionClose
    } = this.props
    const urls = this.getURLS()
    const ownsWorkspace = this.isUserWorkspaceOwner()

    return (
      <div className='MainNavigation'>
        <FlexGrid className='MainNavigation-inner' justifyContent='space-between' alignItems='center'>
          <FlexItem flex='1'>
            <img className='MainNavigation-logo' src={`${ASSET_BASE_URL}/logos/segment.svg`} alt='Segment' />
          </FlexItem>
          <FlexItem flex='1'>
            <ShowHide show={workspace && !hideLeftNav}>
              <FlexGrid justifyContent='center'>
                <FlexItem>
                  <ShowHide show={this.props.shouldShowActivityFeed}>
                    <MainNavigationItem href={urls.activity} active={active === 'activity'} disabled={isLockedOut}>Activity</MainNavigationItem>
                  </ShowHide>
                </FlexItem>
                <FlexItem>
                  <MainNavigationItem href={urls.sources} active={active === 'sources'} disabled={isLockedOut}>Sources</MainNavigationItem>
                </FlexItem>
                <FlexItem>
                  <ShowHide show={ownsWorkspace || user.isAdmin}>
                    <MainNavigationItem href={urls.warehouses} active={active === 'warehouses'} disabled={isLockedOut}>Warehouses</MainNavigationItem>
                  </ShowHide>
                </FlexItem>
                <FlexItem>
                  <ShowHide show={this.props.shouldShowAudiences}>
                    <MainNavigationItem href={urls.users} active={active === 'users'} disabled={isLockedOut}>Audiences</MainNavigationItem>
                  </ShowHide>
                </FlexItem>
                <FlexItem>
                  <ShowHide show={this.props.shouldShowRules}>
                    <MainNavigationItem href={urls.rules} active={active === 'rules'} disabled={isLockedOut}>Event Rules</MainNavigationItem>
                  </ShowHide>
                </FlexItem>
                <FlexItem>
                  <ShowHide show={this.props.shouldShowCustomers}>
                    <MainNavigationItem href={urls.customers} active={active === 'customers'} disabled={isLockedOut}>Users</MainNavigationItem>
                  </ShowHide>
                </FlexItem>
              </FlexGrid>
            </ShowHide>
          </FlexItem>
          <FlexItem flex='1'>
            <FlexGrid alignItems='center' justifyContent='flex-end'>
              <FlexItem>
                {!isLockedOut && workspace &&
                  <AddButton
                    workspaceSlug={workspace.slug}
                    sourcesAvailable={sourcesAvailable}
                    onButtonClick={onAddButtonClick}
                    onLinkClick={onAddLinkClick} />
                }
              </FlexItem>
              <FlexItem>
                {shouldShowWorkspaceNotifications && workspace && <WorkspaceNotificationsApp />}
              </FlexItem>
              <FlexItem>
                <User user={user} workspace={workspace} shouldShowPartnersLink={this.props.shouldShowPartnersLink} partnersLink={this.props.partnersLink} />
              </FlexItem>
            </FlexGrid>
          </FlexItem>
        </FlexGrid>

        {shouldShowSourceRestriction &&
          <Overlay visible>
            <Confirm
              title='Your current plan is limited to 1 Source'
              confirmText='Start Free Trial'
              onClose={onRestrictionClose}
              onConfirm={() => (window.location = `/${workspace.slug}/settings/change-plan`)}
            >
              <p>
                To add unlimited Sources, upgrade to the Team plan for a 14-day.
              </p>
              <Link href='https://segment.com/pricing' rel='external'>
                View pricing page
              </Link>
            </Confirm>
          </Overlay>
        }

        {shouldShowWarehouseRestriction &&
          <Overlay visible>
            <Confirm
              title='Your current plan is limited to 1 Warehouse'
              confirmText='Contact Us'
              onClose={onRestrictionClose}
              onConfirm={() => (window.location = 'https://segment.com/contact/sales')}
            >
              <p>
                To add unlimited Warehouses, upgrade to the Business plan.
              </p>
              <Link href='https://segment.com/pricing' rel='external'>
                View pricing page
              </Link>
            </Confirm>
          </Overlay>
        }
      </div>
    )
  }

  getURLS () {
    const { slug } = this.props.workspace || {}
    return {
      activity: `/${slug}/activity`,
      integrationsCatalog: `/${slug}/integrations/catalog`,
      settings: `/${slug}/settings`,
      simulator: `/${slug}/simulator`,
      rules: `/${slug}/rules/overview`,
      sources: `/${slug}/sources`,
      users: `/${slug}/users`,
      warehouses: `/${slug}/warehouses`,
      customers: `/${slug}/customers`
    }
  }

  isUserWorkspaceOwner () {
    const { user, workspace } = this.props
    if (!workspace) {
      return false
    }
    const { owners = [] } = workspace
    return !!owners.find(i => i === user.id)
  }
}

MainNavigation.propTypes = {
  active: PropTypes.string,
  user: PropTypes.object,
  workspace: PropTypes.object,
  hideLeftNav: PropTypes.bool,
  isLockedOut: PropTypes.bool,
  sourcesAvailable: PropTypes.bool,
  shouldShowSourceRestriction: PropTypes.bool,
  shouldShowWarehouseRestriction: PropTypes.bool,
  shouldShowCustomers: PropTypes.bool,
  onAddButtonClick: PropTypes.func.isRequired,
  onAddLinkClick: PropTypes.func.isRequired,
  onRestrictionClose: PropTypes.func
}

MainNavigation.defaultProps = {
  shouldShowWorkspaceNotifications: false,
  onAddButtonClick: () => {},
  onAddLinkClick: () => {}
}

MainNavigation.displayName = 'MainNavigation'

export default MainNavigation



// WEBPACK FOOTER //
// ./ui/MainNavigation/index.js